import React, { useContext, useState, ChangeEvent, useEffect, useMemo } from 'react';
import { Grid, Box, Checkbox, FormControlLabel, Button, Typography, OutlinedInput, InputLabel, MenuItem, FormControl, ListItemText, FormHelperText } from '@mui/material';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import { useStyles } from './style';
import TextWithToggle from './toggle';
import { TimePicker } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/en-gb';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { MentorshipPreferenceDetails } from '../../interfaces/Profile';
import { AuthContext } from '../../Helpers/Contexts/authContext';
import { AuthUser } from '../../interfaces/Contexts';
import { getMentorShipPreferenceSetting, getPreferenceSetting, postMentorShipPreferenceSetting, postPreferenceSetting, postPreferenceSettings } from '../../Utils/apiService';
import { enqueueSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import routes from '../../Utils/routes';
import { useBlocker } from 'react-router-dom'; // Ensure this is imported
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import CloseIcon from '@mui/icons-material/Close';

dayjs.extend(utc);
dayjs.extend(timezone);

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
    },
  },
};

const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const expertise = ['Project Management'];
const format = 'HH:mm';
const language = ['Tamil', 'English'];
const menteeType = ['Undergraduates', 'Postgraduates'];

interface FormFieldProps {
  isModal?: boolean;
  onSave?: () => void;
}

const FormField: React.FunctionComponent<FormFieldProps> = ({ isModal, onSave }) => {
  const classes = useStyles();
  const {
    state: { user },
  } = useContext(AuthContext);
  const navigate = useNavigate();
  const userData = user as AuthUser;

  // State variables
  const [daySelect, setDaySelect] = React.useState<string[]>([]);
  const [expertiseSelect, setExpertiseSelect] = React.useState<string[]>([]);
  const [languageSelect, setLanguageSelect] = React.useState<string[]>([]);
  const [menteeSelect, setMenteeSelect] = React.useState<string[]>([]);
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>([dayjs('00:00:00.000Z', 'HH:mm'), dayjs('00:00:00.000Z', 'HH:mm')]);
  const [profilePreferences, setprofilePreferences] = useState<{ Profile_Preferences?: string; Id?: string } | null>(null);
  const [formData, setFormData] = useState<MentorshipPreferenceDetails>({
    Mentorship_Availability_Days__c: '',
    Mentorship_Availability_Start_Time__c: '',
    Mentorship_Availability_End_Time__c: '',
    Mentorship_Expertise_Areas__c: '',
    Mentorship_Languages__c: '',
    Mentee_Types__c: '',
    Mentorship_Communication_Preference__c: '',
    Show_Email_To_Mentees__c: false,
    Show_Phone_To_Mentees__c: false,
  });
  const [state, setState] = React.useState({
    Email: false,
    SMS: false,
    Whatsapp: false,
    Inperson: false,
  });
  const [timeError, setTimeError] = useState<string>('');
  const [errors, setErrors] = useState({
    days: false,
    expertise: false,
    language: false,
    menteeType: false,
  });

  // Track initial state
  const [initialFormData, setInitialFormData] = useState<MentorshipPreferenceDetails | null>(null);
  const [initialDaySelect, setInitialDaySelect] = useState<string[]>([]);
  const [initialExpertiseSelect, setInitialExpertiseSelect] = useState<string[]>([]);
  const [initialLanguageSelect, setInitialLanguageSelect] = useState<string[]>([]);
  const [initialMenteeSelect, setInitialMenteeSelect] = useState<string[]>([]);
  const [initialRange, setInitialRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [initialState, setInitialState] = useState<typeof state | null>(null);

  // Navigation blocker states
  const [openModal, setOpenModal] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);
  const [isFormUnchanged, setIsFormUnchanged] = useState(true);

  // Check if any changes have been made
  const isSaveEnabled = useMemo(() => {
    const hasChanges =
      JSON.stringify(formData) !== JSON.stringify(initialFormData) ||
      JSON.stringify(daySelect) !== JSON.stringify(initialDaySelect) ||
      JSON.stringify(expertiseSelect) !== JSON.stringify(initialExpertiseSelect) ||
      JSON.stringify(languageSelect) !== JSON.stringify(initialLanguageSelect) ||
      JSON.stringify(menteeSelect) !== JSON.stringify(initialMenteeSelect) ||
      JSON.stringify(range) !== JSON.stringify(initialRange) ||
      JSON.stringify(state) !== JSON.stringify(initialState);

    setIsFormUnchanged(!hasChanges);
    return hasChanges;
  }, [formData, daySelect, expertiseSelect, languageSelect, menteeSelect, range, state, initialFormData, initialDaySelect, initialExpertiseSelect, initialLanguageSelect, initialMenteeSelect, initialRange, initialState]);

  // Navigation blocker logic
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    return !isFormUnchanged && currentLocation.pathname !== nextLocation.pathname ? true : false;
  });

  useEffect(() => {
    if (blocker.state === 'blocked') {
      setPendingNavigation(blocker?.location?.pathname);
      setOpenModal(true);
    }
  }, [blocker]);

  useEffect(() => {
    if (pendingNavigation) {
      navigate(pendingNavigation);
      setPendingNavigation(null);
    }
  }, [blocker, isFormUnchanged]);

  const confirmNavigation = () => {
    setOpenModal(false);
    setIsFormUnchanged(true);


    if (window.innerWidth <= 768) {
      setTimeout(() => {
        navigate('/mentorship');
      }, 0);
    }
  };



  const cancelNavigation = () => {
    setOpenModal(false);
    setPendingNavigation(null);
  };

  const handleChangeCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
    setState({ ...state, [event.target.name]: event.target.checked });
  };
  const handleChangeDays = (event: SelectChangeEvent<typeof daySelect>) => {
    const {
      target: { value },
    } = event;
    setDaySelect(typeof value === 'string' ? value.split(',') : value);
  };
  const handleChangeExpertise = (event: SelectChangeEvent<typeof expertiseSelect>) => {
    const {
      target: { value },
    } = event;
    setExpertiseSelect(typeof value === 'string' ? value.split(',') : value);
  };
  const handleChangeLanguage = (event: SelectChangeEvent<typeof languageSelect>) => {
    const {
      target: { value },
    } = event;
    setLanguageSelect(typeof value === 'string' ? value.split(',') : value);
  };
  const handleChangeMentee = (event: SelectChangeEvent<typeof menteeSelect>) => {
    const {
      target: { value },
    } = event;
    setMenteeSelect(typeof value === 'string' ? value.split(',') : value);
  };
  const convertTimeToISOStart = (timeStr: string): string => {
    const [hours, minutes] = timeStr.split(':');

    return `${hours}:${minutes}:00.000Z`;
  };
  const convertTimeToISOEnd = (timeStr: string): string => {
    const [hours, minutes] = timeStr.split(':');

    return `${hours}:${minutes}:00.000Z`;
  };
  const handleChangeTime = (times: [Dayjs | null, Dayjs | null] | null, timeStrings: [string, string] | string[]) => {
    if (!times || !times[0] || !times[1]) {
      setTimeError('Please select both start and end time');
      return;
    }

    const startTime = times[0];
    const endTime = times[1];

    // Validate that end time is after start time
    if (endTime.isBefore(startTime)) {
      setTimeError('End time must be after start time');
      setRange(null);
      setFormData({
        ...formData,
        Mentorship_Availability_Start_Time__c: '',
        Mentorship_Availability_End_Time__c: ''
      });
      return;
    }

    // Clear error if validation passes
    setTimeError('');

    console.log(`Start: ${startTime.format(format)}, End: ${endTime.format(format)}`);
    const isoTimeStart = convertTimeToISOStart(`${startTime.format(format)}`);
    const isoTimeEnd = convertTimeToISOEnd(`${endTime.format(format)}`);

    setFormData({
      ...formData,
      Mentorship_Availability_Start_Time__c: isoTimeStart,
      Mentorship_Availability_End_Time__c: isoTimeEnd
    });
    setRange([startTime, endTime]);
  };
  const handleChangeForm = (event: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };
  const handleToggleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    console.log('Toggle Name:', name, 'Value:', checked);

    setFormData((prevState) => ({
      ...prevState,
      [name]: checked,
    }));
  };
  const formatPersonName = (names: string[]): string => {
    return names?.map((name) => name.charAt(0).toUpperCase() + name.slice(1)).join(';');
  };
  const handleSave = () => {
    setOpenModal(false);
    setIsFormUnchanged(true);
    // Reset errors
    setErrors({
      days: daySelect.length === 0,
      expertise: expertiseSelect.length === 0,
      language: languageSelect.length === 0,
      menteeType: menteeSelect.length === 0,
    });

    // Check for validation errors
    if (timeError ||
      daySelect.length === 0 ||
      expertiseSelect.length === 0 ||
      languageSelect.length === 0 ||
      menteeSelect.length === 0) {
      enqueueSnackbar({
        message: 'Please fill in all required fields',
        variant: 'error',
        autoHideDuration: 3000
      });
      return;
    }

    const daysSelect = formatPersonName(daySelect);
    const expertise = formatPersonName(expertiseSelect);
    const languagesSelect = formatPersonName(languageSelect);
    const mentees = formatPersonName(menteeSelect);
    const preferCommunication = Object.keys(state)
      .filter((key) => state[key as keyof typeof state] === true)
      .join(';');
    // Validation for Start Time and End Time
    if (!formData.Mentorship_Availability_Start_Time__c || !formData.Mentorship_Availability_End_Time__c) {
      return enqueueSnackbar({ message: 'Start Time and End Time are required', variant: 'error', autoHideDuration: 1000 });
    }
    if (dayjs(formData.Mentorship_Availability_Start_Time__c).isAfter(dayjs(formData.Mentorship_Availability_End_Time__c))) {
      return enqueueSnackbar({ message: 'Start Time cannot be after End Time', variant: 'error', autoHideDuration: 1000 });
    }
    if (profilePreferences?.Profile_Preferences) {
      const payload: any = {
        ...formData,
        Mentorship_Availability_Days__c: daysSelect,
        Mentorship_Expertise_Areas__c: expertise,
        Mentorship_Languages__c: languagesSelect,
        Mentee_Types__c: mentees,
        Mentorship_Communication_Preference__c: preferCommunication,
        profilePreferences: profilePreferences?.Profile_Preferences
      };
      const isEveryEmpty = Object.keys(payload)
        .filter((items: string) => {
          return items !== 'Show_Phone_To_Mentees__c' && items !== 'Show_Email_To_Mentees__c' && items !== 'accountId';
        })
        .every((items: string) => {
          return items === null || items === '';
        });

      if (isEveryEmpty) {
        return enqueueSnackbar({ message: 'No changes', variant: 'error', autoHideDuration: 1000 });
      }
      postPreferenceSettings(payload)
        .then((res) => {
          if (res?.status === 200) {
            enqueueSnackbar({
              message: 'Mentorship Preference Saved Successfully',
              variant: 'success',
              autoHideDuration: 1000,
            });
            navigate(routes.mentorDetails);
          } else {
            enqueueSnackbar({ message: 'Something went wrong', variant: 'error', autoHideDuration: 1000 });
          }
        })
        .catch((err) => {
          enqueueSnackbar({ message: 'Something went wrong', variant: 'error', autoHideDuration: 1000 });
          console.error(err);
        });
    }
    else {
      const payload: any = {
        ...formData,
        Mentorship_Availability_Days__c: daysSelect,
        Mentorship_Expertise_Areas__c: expertise,
        Mentorship_Languages__c: languagesSelect,
        Mentee_Types__c: mentees,
        Mentorship_Communication_Preference__c: preferCommunication,
        RecordTypeId: profilePreferences?.Id,
        Contact__c: userData?.applicantId
      };
      const isEveryEmpty = Object.keys(payload)
        .filter((items: string) => {
          return items !== 'Show_Phone_To_Mentees__c' && items !== 'Show_Email_To_Mentees__c' && items !== 'accountId';
        })
        .every((items: string) => {
          return items === null || items === '';
        });

      if (isEveryEmpty) {
        return enqueueSnackbar({ message: 'No changes', variant: 'error', autoHideDuration: 1000 });
      }
      postPreferenceSetting(payload)
        .then((res) => {
          if (res?.success === true) {
            enqueueSnackbar({
              message: 'Mentorship Preference Saved Successfully',
              variant: 'success',
              autoHideDuration: 1000,
            });
            navigate(routes.mentorDetails);
          } else {
            enqueueSnackbar({ message: 'Something went wrong', variant: 'error', autoHideDuration: 1000 });
          }
        })
        .catch((err) => {
          enqueueSnackbar({ message: 'Something went wrong', variant: 'error', autoHideDuration: 1000 });
          console.error(err);
        });
    }

    if (isModal && onSave) {
      onSave();
      enqueueSnackbar({
        message: 'Mentorship Preference Saved Successfully',
        variant: 'success',
        autoHideDuration: 1000,
      });
      return;
    }
  };
  const getMentorshipPreferenceSettings = () => {
    getPreferenceSetting(userData.applicantId)
      .then((res) => {
        console.log(res?.data?.mentorshipPreferences, "mentorshipPreferencesmentorshipPreferencesmentorshipPreferences");

        const profilePreferencesId: any = {
          Id: res?.data?.recordTypeIds?.Mentorship_Preferences,
          Profile_Preferences: res?.data?.mentorshipPreferences?.Id
        }
        setprofilePreferences(profilePreferencesId);
        const initialData = {
          Mentorship_Availability_Days__c: res?.data?.mentorshipPreferences.Mentorship_Availability_Days__c,
          Mentorship_Availability_Start_Time__c: res?.data?.mentorshipPreferences.Mentorship_Availability_Start_Time__c,
          Mentorship_Availability_End_Time__c: res?.data?.mentorshipPreferences.Mentorship_Availability_End_Time__c,
          Mentorship_Expertise_Areas__c: res?.data?.mentorshipPreferences.Mentorship_Expertise_Areas__c,
          Mentorship_Languages__c: res?.data?.mentorshipPreferences.Mentorship_Languages__c,
          Mentee_Types__c: res?.data?.mentorshipPreferences.Mentee_Types__c,
          Mentorship_Communication_Preference__c: res?.data?.mentorshipPreferences.Mentorship_Communication_Preference__c,
          Show_Email_To_Mentees__c: res?.data?.mentorshipPreferences.Show_Email_To_Mentees__c,
          Show_Phone_To_Mentees__c: res?.data?.mentorshipPreferences.Show_Phone_To_Mentees__c,
        };

        setFormData(initialData);
        setInitialFormData(initialData);

        const days = res?.data?.mentorshipPreferences.Mentorship_Availability_Days__c.split(';');
        setDaySelect(days);
        setInitialDaySelect(days);

        const expertise = res?.data?.mentorshipPreferences.Mentorship_Expertise_Areas__c.split(';');
        setExpertiseSelect(expertise);
        setInitialExpertiseSelect(expertise);

        const languages = res?.data?.mentorshipPreferences.Mentorship_Languages__c.split(';');
        setLanguageSelect(languages);
        setInitialLanguageSelect(languages);

        const mentee = res?.data?.mentorshipPreferences.Mentee_Types__c.split(';');
        setMenteeSelect(mentee);
        setInitialMenteeSelect(mentee);

        // Handle communication preferences
        const communicationPreferences = res?.data?.mentorshipPreferences.Mentorship_Communication_Preference__c.split(';');
        setState((prevState) => ({
          ...prevState,
          Email: communicationPreferences.includes('Email'),
          SMS: communicationPreferences.includes('SMS'),
          Whatsapp: communicationPreferences.includes('Whatsapp'),
          Inperson: communicationPreferences.includes('Inperson'),
        }));

        if (res?.data?.mentorshipPreferences.Mentorship_Availability_Start_Time__c || res?.data?.mentorshipPreferences.Mentorship_Availability_End_Time__c) {
          const startTime = dayjs(res?.data?.mentorshipPreferences.Mentorship_Availability_Start_Time__c, 'HH:mm');
          const endTime = dayjs(res?.data?.mentorshipPreferences.Mentorship_Availability_End_Time__c, 'HH:mm');
          setRange([startTime, endTime]);
          setInitialRange([startTime, endTime]);
        }
      })
      .catch((err) => {
        console.error(err);
      });
  };

  useEffect(() => {
    getMentorshipPreferenceSettings();
  }, []);
  return (
    <>
      <Box
        className={classes.entireContainer}
        sx={{
          pt: isModal ? 0 : 2,
          pb: isModal ? 0 : 2,
          pr: 2,
          pl: 2,
          maxWidth: isModal ? '900px' : '100%',
          margin: isModal ? '0 auto' : undefined,
          background: isModal ? 'white' : undefined,
        }}
      >

        <Typography
          sx={{
            fontSize: '15px',
            p: 2,
            fontWeight: '500',
            '@media (min-width: 1280px) and (max-width: 1300px)': {
              height : '100vh',
              fontSize: '12px !important',
            },
            '@media (min-width: 1920px) and (max-width: 1940px)': {
              fontSize: '20px !important',
            },
          }}
          textAlign="start"
        >
          Set up your preferences
        </Typography>
        <Box className={`${classes.mentorForm} scrollbar-cusgray`}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={isModal ? 10 : 9}>
              <Grid container className={classes.sepContainer}>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12} textAlign="start">
                  <Typography className={classes.rightHeadings}>
                    Mentorship Availability *
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={12} md={4.5} lg={4.5} xl={4.5}>
                  <Typography textAlign="start" className={classes.labelName} sx={{ marginBottom: { lg: '10px' } }}>
                    Choose days
                  </Typography>
                  <FormControl sx={{ width: '100%', mt: 1 }} error={errors.days}>
                    <Select
                      labelId="demo-multiple-checkbox-label"
                      id="demo-multiple-checkbox"
                      multiple
                      value={daySelect}
                      onChange={handleChangeDays}
                      displayEmpty
                      renderValue={(selected) =>
                        selected.length === 0 ? 'Select an option *' : selected.join(', ')
                      }
                      MenuProps={MenuProps}
                      size="small"
                      sx={{
                        backgroundColor: 'white',
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: errors.days ? '#d32f2f' : '#3061FF',
                        },
                        '.MuiSelect-select': {
                          fontSize: '12px',
                          height: '21px',
                          textAlign: 'left',
                        },
                      }}
                    >
                      {days.map((name) => (
                        <MenuItem
                          key={name}
                          value={name}
                          sx={{
                            '&.Mui-selected': {
                              backgroundColor: 'transparent',
                            },
                            '&.Mui-selected:hover': {
                              backgroundColor: 'transparent',
                            },
                            '&:hover': {
                              backgroundColor: 'transparent',
                            },
                            '&:active': {
                              backgroundColor: 'transparent',
                            },
                          }}
                        >
                          <Checkbox sx={{
                            color: 'black',
                            '&.Mui-checked': {
                              color: '#3061FF',
                            },
                            '& .MuiSvgIcon-root': {
                              fontSize: '18px',
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'black',
                            },
                          }} checked={daySelect.indexOf(name) > -1} />
                          <ListItemText primary={name} />
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.days && (
                      <FormHelperText>This field is required</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={12} md={4.5} lg={4.5} xl={4.5}>
                  <Typography textAlign="start" className={classes.labelName} sx={{ marginBottom: { lg: '10px' } }}>
                    Choose available time *
                  </Typography>
                  <TimePicker.RangePicker
                    format={format + ' A'}
                    showSecond={false}
                    value={range}
                    onChange={handleChangeTime}
                    style={{
                      height: '42px',
                      width: '100%',
                      marginTop: '3px',
                      color: 'rgba(0, 0, 0, 0.8)',
                      border: (!range || timeError) ? '1px solid #d32f2f' : undefined
                    }}
                    size="small"
                    getPopupContainer={trigger => trigger.parentNode as HTMLElement}
                  />
                  {(!range || timeError) && (
                    <Typography
                      sx={{
                        color: '#d32f2f',
                        fontSize: '0.75rem',
                        marginTop: '3px',
                        marginLeft: '14px'
                      }}
                    >
                      {timeError || 'This field is required'}
                    </Typography>
                  )}
                </Grid>
              </Grid>
              <Grid container className={classes.sepContainer}>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12} textAlign="start">
                  <Typography className={classes.rightHeadings}>
                    Areas of Expertise *
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={12} md={4.5} lg={4.5} xl={4.5}>
                  <Typography textAlign="start" className={classes.labelName} sx={{ marginBottom: { lg: '10px' } }}>
                    Select Expertise
                  </Typography>
                  <FormControl sx={{ width: '100%', mt: 1 }} error={errors.expertise}>
                    <Select
                      labelId="demo-multiple-checkbox-label"
                      id="demo-multiple-checkbox"
                      multiple
                      value={expertiseSelect}
                      onChange={handleChangeExpertise}
                      displayEmpty
                      renderValue={(selected) =>
                        selected.length === 0 ? 'Select an option *' : selected.join(', ')
                      }
                      MenuProps={MenuProps}
                      size="small"
                      sx={{
                        backgroundColor: 'white',
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: errors.expertise ? '#d32f2f' : '#3061FF',
                        },
                        '.MuiSelect-select': {
                          fontSize: '12px',
                          height: '21px',
                          textAlign: 'left',
                        },
                      }}
                    >
                      {expertise.map((name) => (
                        <MenuItem
                          key={name}
                          value={name}
                          sx={{
                            backgroundColor: 'white',
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: errors.language ? '#d32f2f' : '#3061FF',
                            },
                            '&.Mui-selected:hover': {
                              backgroundColor: 'transparent',
                            },
                            '&:hover': {
                              backgroundColor: 'transparent',
                            },
                            '&:active': {
                              backgroundColor: 'transparent',
                            },
                          }}
                        >
                          <Checkbox
                            sx={{
                              color: 'black',
                              '&.Mui-checked': {
                                color: '#3061FF',
                              },
                              '& .MuiSvgIcon-root': {
                                fontSize: '18px',
                              },
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'black',
                              },
                            }}
                            checked={expertiseSelect.indexOf(name) > -1} />
                          <ListItemText primary={name} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={12} md={4.5} lg={4.5} xl={4.5}>
                  <Typography textAlign="start" className={classes.labelName} sx={{ marginBottom: { lg: '10px' } }}>
                    Select Languages
                  </Typography>
                  <FormControl sx={{ width: '100%', mt: 1 }} error={errors.language}>
                    <Select
                      labelId="demo-multiple-checkbox-label"
                      id="demo-multiple-checkbox"
                      multiple
                      value={languageSelect}
                      onChange={handleChangeLanguage}
                      displayEmpty
                      renderValue={(selected) =>
                        selected.length === 0 ? 'Select an option *' : selected.join(', ')
                      }
                      MenuProps={MenuProps}
                      size="small"
                      sx={{
                        backgroundColor: 'white',
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: errors.language ? '#d32f2f' : '#3061FF',
                        },
                        '.MuiSelect-select': {
                          fontSize: '12px',
                          height: '21px',
                          textAlign: 'left',
                        },
                      }}
                    >
                      {language.map((name) => (
                        <MenuItem key={name} value={name}>
                          <Checkbox
                            sx={{
                              color: 'black',
                              '&.Mui-checked': {
                                color: '#3061FF',
                              },
                              '& .MuiSvgIcon-root': {
                                fontSize: '18px',
                              },
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'black',
                              },
                            }}
                            checked={languageSelect.indexOf(name) > -1} />
                          <ListItemText primary={name} />
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.language && (
                      <FormHelperText>This field is required</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              </Grid>
              <Grid container className={classes.sepContainer}>
                <Grid item xs={12} textAlign="start">
                  <Typography className={classes.rightHeadings}>
                    MentorShip Preferences *
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={12} md={4.5} lg={4.5} xl={4.5}>
                  <Typography textAlign="start" className={classes.labelName} sx={{ marginBottom: { lg: '10px' } }}>
                    Types of Mentees
                  </Typography>
                  <FormControl sx={{ width: '100%', mt: 1 }} error={errors.menteeType}>
                    <Select
                      labelId="demo-multiple-checkbox-label"
                      id="demo-multiple-checkbox"
                      multiple
                      value={menteeSelect}
                      onChange={handleChangeMentee}
                      displayEmpty
                      renderValue={(selected) =>
                        selected.length === 0 ? 'Select an option *' : selected.join(', ')
                      }
                      MenuProps={MenuProps}
                      size="small"
                      sx={{
                        backgroundColor: 'white',
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: errors.menteeType ? '#d32f2f' : '#3061FF',
                        },
                        '.MuiSelect-select': {
                          fontSize: '12px',
                          height: '21px',
                          textAlign: 'left',
                        },
                      }}
                    >
                      {menteeType.map((name) => (
                        <MenuItem key={name} value={name}>
                          <Checkbox
                            sx={{
                              color: 'black',
                              '&.Mui-checked': {
                                color: '#3061FF',
                              },
                              '& .MuiSvgIcon-root': {
                                fontSize: '18px',
                              },
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'black',
                              },
                            }}
                            checked={menteeSelect.indexOf(name) > -1} />
                          <ListItemText primary={name} />
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.menteeType && (
                      <FormHelperText>This field is required</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={12} md={isModal ? 7.5 : 6} lg={isModal ? 7.5 : 6} xl={isModal ? 7.5 : 6}>
                  <Typography textAlign="start" className={classes.labelName} sx={{ marginBottom: { lg: '10px' } }}>
                    Preferred method of communication
                  </Typography>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'start', marginTop: '18px' }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={state.Email}
                          onChange={handleChangeCheck}
                          name="Email"
                          sx={{
                            color: 'black',
                            '&.Mui-checked': {
                              color: '#3061FF',
                            },
                            '& .MuiSvgIcon-root': {
                              fontSize: '18px',
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'black',
                            },
                          }}
                        />
                      }
                      label={<Typography sx={{ fontSize: '12px', fontWeight: 500 }}>Email</Typography>}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={state.SMS}
                          onChange={handleChangeCheck}
                          name="SMS"
                          sx={{
                            color: 'black',
                            '&.Mui-checked': {
                              color: '#3061FF',
                            },
                            '& .MuiSvgIcon-root': {
                              fontSize: '18px',
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'black',
                            },
                          }}
                        />
                      }
                      label={<Typography sx={{ fontSize: '12px', fontWeight: 500 }}>SMS</Typography>}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={state.Whatsapp}
                          onChange={handleChangeCheck}
                          name="Whatsapp"
                          sx={{
                            color: 'black',
                            '&.Mui-checked': {
                              color: '#3061FF',
                            },
                            '& .MuiSvgIcon-root': {
                              fontSize: '18px',
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'black',
                            },
                          }}
                        />
                      }
                      label={<Typography sx={{ fontSize: '12px', fontWeight: 500 }}>Whatsapp</Typography>}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={state.Inperson}
                          onChange={handleChangeCheck}
                          name="Inperson"
                          sx={{
                            color: 'black',
                            '&.Mui-checked': {
                              color: '#3061FF',
                            },
                            '& .MuiSvgIcon-root': {
                              fontSize: '18px',
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: 'black',
                            },
                          }}
                        />
                      }
                      label={<Typography sx={{ fontSize: '12px', fontWeight: 500 }}>In-person meeting</Typography>}
                    />
                  </div>
                </Grid>
              </Grid>
              <Grid container className={classes.sepContainer}>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12} textAlign="start" sx={{ marginBottom: { lg: '10px' } }}>
                  <Typography className={classes.rightHeadings}>Privacy and confidentiality</Typography>
                </Grid>
                <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                  <Typography textAlign="start" className={classes.labelName} sx={{ marginBottom: { lg: '10px' } }}>
                    Manage who can send your contact information
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={4.5} xl={4.5}>
                  <TextWithToggle toggleName={'Show_Email_To_Mentees__c'} isChecked={formData.Show_Email_To_Mentees__c} handleToggleChange={handleToggleChange} name="Show_Email_To_Mentees__c" />
                </Grid>
                <Grid item xs={12} sm={12} md={6} lg={4.5} xl={4.5}>
                  <TextWithToggle toggleName={'Show_Phone_To_Mentees__c'} isChecked={formData.Show_Phone_To_Mentees__c} handleToggleChange={handleToggleChange} name="Show_Phone_To_Mentees__c" />
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={3}></Grid>
          </Grid>
        </Box>
        <hr style={{ border: '1px solid #E5E6EA', marginTop: "10px" }} />
        <Box sx={{
          position: 'fixed',
          bottom: 36, // adds a bit of spacing from bottom
          right: 16,  // adds spacing from right
          width: '100%', // ensures full width
          zIndex: 1200,
          px: 2,
          textAlign:'end',
          py: 1,
        }}>
          <Button
            sx={{
              background: 'white',
              color: '#373A45',
              fontSize: '12px',
              fontWeight: '500',
              backgroundColor: 'white',
              '&:hover': {
                backgroundColor: 'white',
              },
              '&:active': {
                backgroundColor: 'white',
              },
            }}
            onClick={() => {
              navigate(-1);
            }}
          >
            <Typography
              sx={{
                fontSize: '14px',
                fontWeight: 500,
                padding: '3px 15px',
                '@media (min-width: 1280px) and (max-width: 1300px)': {
                  fontSize: '12.5px !important',
                },
              }}
            >
              Cancel
            </Typography>
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            // disabled={!isSaveEnabled}
            sx={{
              ml: 2,
              borderRadius: '6px',
              boxShadow: 'none',
              '&:hover': {
                boxShadow: 'none',
              },
              '&:active': {
                boxShadow: 'none',
              },
            }}
          >
            <Typography
              sx={{
                fontSize: '14px',
                padding: '3px 15px',
                fontWeight: 500,
                color: '#5B4200',
                '@media (min-width: 1280px) and (max-width: 1300px)': {
                  fontSize: '12.5px !important',
                },
              }}
            >
              Save
            </Typography>
          </Button>
        </Box>
      </Box>
      {!isModal && (
        <Dialog open={openModal} onClose={cancelNavigation}>
          <DialogTitle>
            <Typography
              sx={{
                fontSize: '20px',
                color: '#373A45',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: '1px solid #E0E0E0',
              }}
            >
              Please note!
              <Button
                onClick={cancelNavigation}
                sx={{
                  minWidth: 'auto',
                  padding: 0,
                  background: 'transparent',
                  '&:hover': {
                    background: 'transparent',
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: '20px', fontWeight: 'bold', color: '#373A45' }} />
              </Button>
            </Typography>
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              <Typography sx={{ fontSize: '12px', fontWeight: '500', color: '#373A45' }}>
                All your changes will be lost. If you navigate away, you will lose progress. Are you sure you want to continue?
              </Typography>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              sx={{
                background: 'white',
              }}
              onClick={cancelNavigation}
            >
              <Typography sx={{ fontSize: '12px', fontWeight: '500', color: '#373A45' }}>Cancel</Typography>
            </Button>
            <Button
              variant="contained"
              onClick={confirmNavigation}
              sx={{
                ml: 2,
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: 'none',
                },
              }}
            >
              <Typography sx={{ fontSize: '12px', fontWeight: '500',color:'#5B4200'  }}>Yes, continue</Typography>
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};
export default FormField;
